import { useCallback, useEffect, useState } from "react";
import ItemDTO from "../dtos/ItemDTO";
import facade from "../services/qwof/apiFacade";
import { enrichItemsWithTradingPostPrices } from "../services/gw2API2/gw2API2";

const DEFAULT_GUILD_ID = "DA835590-BFBA-4D4C-BF10-014905F32E00";
const DEFAULT_STASH_TAB_ID = 0;

const cloneItems = (items) => items.map((item) => ({ ...item }));

export default function useGuildStashInventory({ isLoggedIn, username }) {
  const [loadedItems, setLoadedItems] = useState([]);
  const [stashItems, setStashItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [isIqMember, setIsIqMember] = useState(false);

  const loadItems = useCallback(async () => {
    if (!isLoggedIn) {
      setLoadedItems([]);
      setStashItems([]);
      setLoadError("Login to see Guild stash");
      setLoading(false);
      return;
    }

    setLoading(true);
    const loadStart = Date.now();
    setLoadError("");

    try {
      const res = await facade.getGuildStashTab(DEFAULT_GUILD_ID, DEFAULT_STASH_TAB_ID);
      const stacks = Array.isArray(res) ? res : Array.isArray(res?.stacks) ? res.stacks : [];

      const dtos = stacks.map((stack) => {
        const apiItem = stack.item || {};
        return new ItemDTO({
          id: apiItem.gw2ApiId ?? apiItem.id ?? stack.id,
          count: stack.count ?? 0,
          name: apiItem.name,
          type: apiItem.type,
          rarity: apiItem.rarity,
          vendor_value: apiItem.vendorValue ?? apiItem.vendor_value ?? 0,
          chat_link: apiItem.chatLink ?? apiItem.chat_link,
          icon: apiItem.icon ?? stack.icon ?? null,
          image_url:
            apiItem.imageUrl ??
            apiItem.image_url ??
            stack.imageUrl ??
            stack.image_url ??
            null,
          details: apiItem.details ?? null,
        });
      });

      const pricedDtos = await enrichItemsWithTradingPostPrices(dtos);

      setLoadedItems(cloneItems(pricedDtos));
      setStashItems(cloneItems(pricedDtos));
    } catch (err) {
      if (err && err.status === 401) {
        setLoadError("Login to see Guild stash");
      } else if (err && err.status === 403) {
        setLoadError("missing_api_key");
      } else {
        let msg = err?.fullError ?? err?.message ?? "";

        if (msg && typeof msg === "object") {
          if (typeof msg.msg === "string") {
            msg = msg.msg;
          } else {
            msg = JSON.stringify(msg);
          }
        }

        if (
          typeof msg === "string" &&
          msg.includes("Unauthorized. Could not verify token")
        ) {
          setLoadError("Login to see Guild stash");
        } else {
          setLoadError(msg || "Failed to load items");
        }
      }
    } finally {
      const elapsed = Date.now() - loadStart;
      const minDuration = 1000;
      if (elapsed < minDuration) {
        setTimeout(() => setLoading(false), minDuration - elapsed);
      } else {
        setLoading(false);
      }
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const guildIqId = "DA835590-BFBA-4D4C-BF10-014905F32E00";

    if (!isLoggedIn || !username) {
      setIsIqMember(false);
      return;
    }

    facade
      .isMemberOfGuild(guildIqId, username)
      .then((res) => {
        setIsIqMember(!!res);
      })
      .catch(() => {
        setIsIqMember(false);
      });
  }, [isLoggedIn, username]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    let timerId;
    if (loading && loadError !== "missing_api_key") {
      timerId = setTimeout(() => setShowSpinner(true), 250);
    } else {
      setShowSpinner(false);
    }
    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [loading, loadError]);

  return {
    isIqMember,
    loadError,
    loadedItems,
    loading,
    loadItems,
    setStashItems,
    showSpinner,
    stashItems,
  };
}